/**
 * Single source of truth for the RBAC permission-key catalogues.
 *
 * Shared by the server (re-exported from
 * src/server/middleware/portal/field-metadata.ts) and the client
 * (re-exported from src/client/views/portal/utils/rbac-keys.ts) so the role
 * editor can never drift out of sync with the keys the server recognises.
 *
 * This module must stay dependency-free (src/common may not import from a
 * sub-app directory).
 */

/**
 * Historical (TASK-476): the short-lived own-scoped "Oma andmed" system role,
 * replaced by the per-field `can_edit_own` axis. Referenced only by the
 * migrations that created and later dropped it.
 */
export const OWN_DATA_ROLE_NAME = 'Oma andmed'

export const WORKSPACE_PERMISSION_KEYS = [
    'people_directory',
    'org_chart',
    'everyone_birthday',
    'employment_start_date',
    'access_reports',
    'create_reports',
    'access_shared_reports',
    'create_shared_reports',
    // Visibility of OTHER people's "Tööaeg" / "Projektid" profile tabs. Direct
    // managers always see their own reports' tabs regardless of these grants.
    'view_worktime',
    'view_projects',
] as const

export const PROFILE_PERMISSION_KEYS = [
    'see_new_hires',
    'delete_new_hires',
    'add_members',
    'send_invites',
    'see_offboarded_users',
    'offboard_users',
    'delete_offboarded_users',
] as const

export const ADMIN_PERMISSION_KEYS = [
    'upload_company_documents',
    'manage_document_types',
    'manage_custom_fields',
    'manage_time_away_settings',
    'manage_roles',
    'manage_organisation_settings',
    'manage_daily_digest',
    'view_audit_log',
    'view_error_log',
    'view_as',
] as const

export const DOCUMENT_TYPES = [
    'arenguvestlus',
    'bonus',
    'commission',
    'cv',
    'employment_agreement',
    'exit_interview',
    'other',
    'praktikaleping',
    'praktika_retro',
    'rahulolu_uuring',
    'share_options',
    'tiimijuhi_markmed',
    'tooelulugu',
    'toolepingu_lisa',
    'volikiri',
] as const

export type WorkspacePermissionKey = (typeof WORKSPACE_PERMISSION_KEYS)[number]
export type ProfilePermissionKey = (typeof PROFILE_PERMISSION_KEYS)[number]
export type AdminPermissionKey = (typeof ADMIN_PERMISSION_KEYS)[number]
export type DocumentType = (typeof DOCUMENT_TYPES)[number]
